import { useEffect, useMemo, useState } from 'react';
import type { DocumentBlock, DocumentPreview, ProjectDocument } from '../lib/types';
import { api, ApiError } from '../lib/api';
import { formatDate } from '../lib/dates';

/**
 * A document read on screen, instead of saved and opened in something else.
 *
 * TWO KINDS OF DOCUMENT ARRIVE HERE, and they are shown in opposite ways.
 *
 *   A PDF, an image or a text file is the FILE ITSELF, served by `/documents/:id/view` under
 *   a sandboxing CSP so it cannot act as a page in the Hub's own origin.
 *
 *   A Word, Excel or PowerPoint file never reaches the browser at all — no browser renders a
 *   zip archive. The API reads it into data and this draws that data with its own markup, so
 *   nothing in the document is interpreted: not a formula, not a link, not a macro.
 *
 * Which one a document is comes from the API's `viewable`, never from the filename.
 *
 * THE DOWNLOAD IS STILL OFFERED HERE. Reading it and keeping a copy are different needs, and
 * a viewer that swallows the file is a viewer somebody works around — doubly so for a
 * preview, which is deliberately not the whole file.
 */
export function DocumentViewer({
  document: record,
  onClose,
  route = 'documents',
}: {
  document: ProjectDocument;
  onClose: () => void;
  /**
   * Where the file is served from: `documents` for a project's, `risk-documents` for a risk's.
   * The same three routes either way — view, download and preview.
   */
  route?: 'documents' | 'risk-documents';
}) {
  const [failed, setFailed] = useState(false);
  const source = `/api/${route}/${record.id}/view`;

  const extracted =
    record.viewable === 'sheet' || record.viewable === 'doc' || record.viewable === 'slides';

  /**
   * Escape closes it, and the page behind does not scroll while it is open.
   *
   * Restored on the way out rather than assigned blank, so a nested overlay that also touched
   * it does not leave the page unscrollable.
   */
  useEffect(() => {
    const previous = window.document.body.style.overflow;
    window.document.body.style.overflow = 'hidden';
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.document.body.style.overflow = previous;
    };
  }, [onClose]);

  return (
    <div
      className="doc-viewer-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={record.title}
      // Clicking the darkness closes it; clicking the document itself must not.
      onClick={onClose}
    >
      <div className="doc-viewer" onClick={(event) => event.stopPropagation()}>
        <div className="doc-viewer-head">
          <span className="grow" style={{ minWidth: 0 }}>
            <strong>{record.title}</strong>
            <div className="doc-meta">
              {record.originalName}
              {record.uploadedBy ? ` · ${record.uploadedBy.name}` : ''}
              {record.signedOn ? ` · signed ${formatDate(record.signedOn)}` : ''}
            </div>
          </span>
          <a href={`/api/${route}/${record.id}/download`} className="btn btn-quiet btn-sm">
            Download
          </a>
          <button type="button" className="btn btn-quiet btn-sm" onClick={onClose}>
            Close
          </button>
        </div>

        <div className={extracted ? 'doc-viewer-body doc-viewer-body-read' : 'doc-viewer-body'}>
          {extracted ? (
            <ExtractedDocument document={record} route={route} />
          ) : failed ? (
            /**
             * Said plainly rather than left as a blank frame. A browser with its PDF viewer
             * turned off renders nothing and reports nothing, which reads as a broken app.
             */
            <div className="empty">
              <h3>This browser will not show it</h3>
              <p>
                Nothing is wrong with the file — download it and open it in whatever you normally
                use.
              </p>
              <a href={`/api/${route}/${record.id}/download`} className="btn btn-sm">
                Download {record.originalName}
              </a>
            </div>
          ) : record.viewable === 'image' ? (
            <img src={source} alt={record.title} onError={() => setFailed(true)} />
          ) : (
            /* A frame for the PDF and the text, so the browser's own reader does the work. */
            <iframe
              src={source}
              title={record.title}
              /**
               * NO `sandbox` ATTRIBUTE ON A PDF, and it took measuring to believe it.
               *
               * Chrome showed "This page has been blocked by Chrome" instead of the document.
               * The suspect was the response's CSP — its viewer is a scripted document and we
               * send `script-src 'none'` — but serving one PDF eleven ways proved otherwise:
               * Chrome renders it under our strictest policy, and refuses it whenever this
               * attribute is present, `allow-scripts` included.
               *
               * NOTHING IS GIVEN UP BY DROPPING IT. The response still carries `sandbox` in
               * its own CSP, which is what puts the file in an opaque origin with no script,
               * no forms and no API — and a header cannot be forgotten by a change up here.
               * Text keeps the attribute as well, since nothing about it needs a viewer.
               */
              sandbox={record.viewable === 'pdf' ? undefined : ''}
              onError={() => setFailed(true)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/** A Word, Excel or PowerPoint file, fetched as data and drawn here. */
function ExtractedDocument({
  document: record,
  route,
}: {
  document: ProjectDocument;
  route: 'documents' | 'risk-documents';
}) {
  const [preview, setPreview] = useState<DocumentPreview | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setPreview(null);
    setProblem(null);

    api
      .get<DocumentPreview>(`/${route}/${record.id}/preview`)
      .then((payload) => {
        if (live) setPreview(payload);
      })
      .catch((error: unknown) => {
        if (!live) return;
        /**
         * The API's own words. It refuses a preview for reasons worth saying — too large to
         * read here, nothing readable inside — and "something went wrong" would send somebody
         * looking for a fault that is not there.
         */
        setProblem(error instanceof ApiError ? error.message : 'That file could not be read here');
      });

    return () => {
      live = false;
    };
  }, [record.id, route]);

  if (problem) {
    return (
      <div className="empty">
        <h3>This one cannot be shown here</h3>
        <p>{problem}</p>
        <a href={`/api/${route}/${record.id}/download`} className="btn btn-sm">
          Download {record.originalName}
        </a>
      </div>
    );
  }

  if (!preview) return <p className="dim doc-preview-loading">Reading the file…</p>;

  return (
    <div className="doc-preview">
      {preview.kind === 'sheet' ? (
        <Workbook preview={preview} />
      ) : preview.kind === 'doc' ? (
        <WordDocument blocks={preview.blocks} moreBlocks={preview.moreBlocks} />
      ) : (
        <Deck slides={preview.slides} moreSlides={preview.moreSlides} />
      )}

      {/*
        Said on every preview, because a preview that looks like the document is a preview
        somebody quotes from without checking.
      */}
      <p className="doc-preview-note">
        A preview of the text and values only — no layout, images or formatting.{' '}
        <a href={`/api/${route}/${record.id}/download`}>Download the file</a> for the original.
      </p>
    </div>
  );
}

function Workbook({ preview }: { preview: Extract<DocumentPreview, { kind: 'sheet' }> }) {
  const [active, setActive] = useState(0);
  const sheet = preview.sheets[Math.min(active, preview.sheets.length - 1)];

  return (
    <>
      {preview.sheets.length > 1 && (
        <div className="doc-preview-tabs" role="tablist">
          {preview.sheets.map((entry, index) => (
            <button
              key={entry.name + index}
              type="button"
              role="tab"
              aria-selected={index === active}
              className={index === active ? 'chip-toggle is-on' : 'chip-toggle'}
              onClick={() => setActive(index)}
            >
              {entry.name}
            </button>
          ))}
        </div>
      )}

      {sheet.rows.length === 0 ? (
        <p className="dim">This sheet is empty.</p>
      ) : (
        <div className="doc-preview-scroll">
          <table className="data doc-preview-grid">
            <tbody>
              {sheet.rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell, cellIndex) => (
                    /*
                      The first row is drawn as a heading because nearly every sheet has one —
                      but as a `td`, not a `th`: whether it is really a header is the file's
                      business, and calling it one out loud would put it in a screen reader's
                      mouth as fact.
                    */
                    <td key={cellIndex} className={rowIndex === 0 ? 'doc-preview-first' : undefined}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <LeftOut
        parts={[
          sheet.moreRows ? `${sheet.moreRows} more rows` : '',
          sheet.moreColumns ? `${sheet.moreColumns} more columns` : '',
          preview.moreSheets ? `${preview.moreSheets} more sheets` : '',
        ]}
      />
    </>
  );
}

function WordDocument({ blocks, moreBlocks }: { blocks: DocumentBlock[]; moreBlocks: number }) {
  /** Consecutive bullets become one list, so a five-point list is not five separate ones. */
  const grouped = useMemo(() => groupBullets(blocks), [blocks]);

  return (
    <>
      <div className="doc-preview-page">
        {grouped.map((block, index) => {
          if (block.type === 'bullets') {
            return (
              <ul key={index}>
                {block.items.map((item, itemIndex) => (
                  <li key={itemIndex}>{item}</li>
                ))}
              </ul>
            );
          }

          if (block.type === 'table') {
            return (
              <div className="doc-preview-scroll" key={index}>
                <table className="data doc-preview-grid">
                  <tbody>
                    {block.rows.map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        {row.map((cell, cellIndex) => (
                          <td key={cellIndex} className={rowIndex === 0 ? 'doc-preview-first' : undefined}>
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          }

          if (block.type === 'heading') {
            /* Levels 1 and 2 lead; deeper ones are still headings but stop growing. */
            return block.level <= 1 ? (
              <h3 key={index}>{block.text}</h3>
            ) : block.level === 2 ? (
              <h4 key={index}>{block.text}</h4>
            ) : (
              <h5 key={index}>{block.text}</h5>
            );
          }

          return <p key={index}>{block.text}</p>;
        })}
      </div>

      <LeftOut parts={[moreBlocks ? `${moreBlocks} more paragraphs` : '']} />
    </>
  );
}

function Deck({
  slides,
  moreSlides,
}: {
  slides: { number: number; lines: string[] }[];
  moreSlides: number;
}) {
  return (
    <>
      <div className="stack-sm">
        {slides.map((slide) => (
          <div className="doc-preview-slide" key={slide.number}>
            <span className="doc-preview-slide-number">Slide {slide.number}</span>
            {slide.lines.length === 0 ? (
              <p className="dim">No text on this slide.</p>
            ) : (
              <>
                <strong>{slide.lines[0]}</strong>
                {slide.lines.length > 1 && (
                  <ul>
                    {slide.lines.slice(1).map((line, index) => (
                      <li key={index}>{line}</li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        ))}
      </div>

      <LeftOut parts={[moreSlides ? `${moreSlides} more slides` : '']} />
    </>
  );
}

/** What the preview stopped short of, named rather than left to be discovered. */
function LeftOut({ parts }: { parts: string[] }) {
  const said = parts.filter(Boolean);
  if (said.length === 0) return null;

  return (
    <p className="doc-preview-more">
      Not shown here: {said.join(', ')}. Download the file to see all of it.
    </p>
  );
}

type Grouped = DocumentBlock | { type: 'bullets'; items: string[] };

function groupBullets(blocks: DocumentBlock[]): Grouped[] {
  const out: Grouped[] = [];

  for (const block of blocks) {
    if (block.type !== 'bullet') {
      out.push(block);
      continue;
    }

    const last = out[out.length - 1];
    if (last && last.type === 'bullets') last.items.push(block.text);
    else out.push({ type: 'bullets', items: [block.text] });
  }

  return out;
}
